import { Fragment, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export const MESSAGE_TYPES = Object.freeze({
    INFO: "INFO",
    SUCCESS: "SUCCESS",
    WARN: "WARN",
    ERROR: "ERROR",
} as const);

export type MessageType = (typeof MESSAGE_TYPES)[keyof typeof MESSAGE_TYPES];

export interface MessageEntry {
    title?: ReactNode;
    description?: ReactNode;
}

interface MessageProps {
    type?: MessageType;
    entries: MessageEntry[];
    className?: string;
}

const TYPE_CONFIG = {
    [MESSAGE_TYPES.INFO]: {
        container: "bg-info-50",
        accent: "bg-info-500",
        icon: "pi-info-circle",
        color: "text-info-500",
        labelKey: "common:info",
    },
    [MESSAGE_TYPES.SUCCESS]: {
        container: "bg-success-50",
        accent: "bg-success-500",
        icon: "pi-check",
        color: "text-success-500",
        labelKey: "common:success",
    },
    [MESSAGE_TYPES.WARN]: {
        container: "bg-warning-50",
        accent: "bg-warning-500",
        icon: "pi-exclamation-triangle",
        color: "text-warning-500",
        labelKey: "common:warning",
    },
    [MESSAGE_TYPES.ERROR]: {
        container: "bg-error-35",
        accent: "bg-error-500",
        icon: "pi-exclamation-circle",
        color: "text-error-500",
        labelKey: "common:error",
    },
};

export const Message = (props: MessageProps) => {
    const { type = MESSAGE_TYPES.INFO, entries, className } = props;

    const { t } = useTranslation("common");

    const typeConfig = TYPE_CONFIG[type];

    return (
        <div
            className={twMerge(
                "relative grid grid-cols-[auto_1fr] gap-x-1.5 gap-y-3.5 w-full overflow-hidden rounded-md pl-3.5 pr-1.5 py-2.5",
                typeConfig.container,
                className
            )}
        >
            <div
                className={twMerge(
                    "absolute inset-y-0 left-0 w-0.75 rounded-l-md",
                    typeConfig.accent
                )}
            />
            {entries.map((entry, index) => (
                <Fragment key={index}>
                    <div
                        className={twMerge(
                            "flex flex-col items-center pt-1",
                            typeConfig.color
                        )}
                    >
                        <i
                            className={twMerge(
                                "pi text-lg leading-none size-4 flex items-center justify-center",
                                typeConfig.icon
                            )}
                        />
                        <span className="text-xs leading-none font-bold mt-1">
                            {t(typeConfig.labelKey)}
                        </span>
                    </div>
                    <div className="flex flex-col gap-1 text-text-500">
                        {entry.title && (
                            <span className="leading-5 font-bold">
                                {entry.title}
                            </span>
                        )}
                        {entry.description && (
                            <div className="leading-5">
                                {entry.description}
                            </div>
                        )}
                    </div>
                </Fragment>
            ))}
        </div>
    );
};
